import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  Form,
  CardBody,
  CardSubtitle,
  Container,
  Table,
  Alert,
  FormGroup,
  Label,
} from "reactstrap"
import Dropzone from "react-dropzone"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { Link, useNavigate } from "react-router-dom"
import { useLoading } from "helpers/custom_hooks"
import { UserInfoError, displayMessage } from "helpers/misc_helpers"
import _ from "lodash"
import * as XLSX from "xlsx"
import MessageModal from "components/Common/MessageModal"
import Select from "react-select"
import UploadFile from "./LOA-Upload.xlsx"
import {
  GetPartnerCockpits,
  UploadLOAList,
  GetMyUser,
} from "helpers/backend_helper"

// Dummy data for development - replace with actual API call
const dummyPartnerCockpits = [
  {
    id: 1,
    name: "Automotive Supply Chain",
  },
  {
    id: 2,
    name: "Electronics Manufacturing",
  },
  {
    id: 3,
    name: "Industrial Equipment",
  },
]

const LOAUpload = props => {
  const setLoading = useLoading()
  const navigate = useNavigate()
  const [error, setError] = useState()
  const [selectedFiles, setselectedFiles] = useState([])
  const [data, setData] = useState([])
  const [successModal, setSuccessModal] = useState(false)
  const [showLOAs, setShowLOAs] = useState(false)
  const [selectedCockpit, setSelectedCockpit] = useState(null)
  const [partnerCockpits, setPartnerCockpits] = useState([])
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Convert partner cockpits to select options
  const cockpitOptions = partnerCockpits.map(cockpit => ({
    value: cockpit.id,
    label: cockpit.name,
  }))

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [cockpitsData, userData] = await Promise.all([
        GetPartnerCockpits(),
        GetMyUser(),
      ])
      setPartnerCockpits(cockpitsData)
      setUser(userData)
    } catch (error) {
      console.error("Error loading data:", error)
    } finally {
      setLoading(false)
    }
  }

  // Check if user is admin in selected cockpit
  useEffect(() => {
    if (selectedCockpit && user) {
      const cockpit = partnerCockpits.find(c => c.id === selectedCockpit.value)
      setIsAdmin(cockpit.role == "Admin")
    } else {
      setIsAdmin(false)
    }
  }, [selectedCockpit, user, partnerCockpits])

  const handleFileUpload = files => {
    const file = files[0]
    Object.assign(file, {
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
    })

    const reader = new FileReader()

    reader.onload = e => {
      const fileData = e.target.result
      parseExcel(fileData)
    }

    reader.readAsArrayBuffer(file)
    setselectedFiles(files)
  }

  const parseExcel = fileData => {
    try {
      const workbook = XLSX.read(fileData, { type: "array" })
      const sheetName = workbook.SheetNames[0]
      const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName])

      const data = MapToLOAList(sheetData)
      setData(data)
    } catch (error) {
      if (error instanceof UserInfoError)
        return displayMessage(error.message, setError)
      else displayMessage("An unexpected error happened!", setError)
    }
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]

    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
  }

  const MapToLOAList = csvData => {
    const result = []

    csvData.forEach((e, index) => {
      var price = getFloat(e["price"])
      var moq = getFloat(e["moq"])

      if (!moq)
        throw new UserInfoError(
          "MOQ in row " + (index + 1) + " is not a valid number!"
        )

      if (e["price"] && !price)
        throw new UserInfoError(
          "Price in row " + (index + 1) + " is not a valid number!"
        )

      var newRow = {
        cpn: e["cpn"] ? "" + e["cpn"] : "",
        partNumber: e["manufacturer part number"]
          ? e["manufacturer part number"] + ""
          : "",
        manufacturer: e["manufacturer"],
        supplier: e["supplier"],
        price,
        currency: e["currency"] ? "" + e["currency"] : "EUR",
        moq: moq,
        leadTime: e["lead time"],
        packagingUnit: e["packaging unit"],
        notes: e["notes"] ? e["notes"] + "" : "",
      }

      if (!newRow.manufacturer)
        throw new UserInfoError(
          "Manufacturer in row " + (index + 1) + " is not defined!"
        )

      if (!newRow.partNumber)
        throw new UserInfoError(
          "Manufacturer Part Number in row " + (index + 1) + " is not defined!"
        )

      if (!newRow.supplier)
        throw new UserInfoError(
          "Supplier in row " + (index + 1) + " is not defined!"
        )

      result.push(_.omitBy(newRow, _.isNil))
    })

    return result
  }

  const getFloat = s => {
    try {
      var number = parseFloat(s)
      if (isNaN(number)) return null
      return number
    } catch (error) {
      return null
    }
  }

  const handleSave = async () => {
    if (!selectedCockpit) {
      displayMessage("Please select a Partner Cockpit!", setError)
      return
    }

    if (!isAdmin) {
      displayMessage("You need to be an admin to upload LOAs!", setError)
      return
    }

    setLoading(true)
    try {
      await UploadLOAList({
        loaEntries: data.map(entry => ({
          cpn: entry.cpn,
          mpn: entry.partNumber,
          manufacturer: entry.manufacturer,
          supplier: entry.supplier,
          price: entry.price,
          currency: entry.currency,
          moq: entry.moq,
          leadTime: entry.leadTime,
          packagingUnit: entry.packagingUnit,
          notes: entry.notes,
        })),
        partnerCockpitID: selectedCockpit.value,
      })

      setSuccessModal(true)
    } catch (error) {
      error?.response?.status == 400
        ? displayMessage(error.response.data, setError)
        : displayMessage("An unexpected error happened!", setError)
      console.error(error)
    }

    setLoading(false)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title={"Partner Cockpit"}
            breadcrumbItem={"LOA Masterfile Upload"}
          />

          {error && <Alert color="danger">{error}</Alert>}
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row>
                    <Col lg="6">
                      <h6 className="card-title">Upload LOA Masterfile</h6>
                      <CardSubtitle className="mb-3">
                        Please download our template and fill it up with your
                        data. Afterwards you can upload it here and we will
                        process your data.
                      </CardSubtitle>
                    </Col>
                    <Col className="d-flex align-items-center justify-content-end">
                      <a
                        href={UploadFile}
                        className="btn btn-primary"
                        target="_blank"
                        rel="noreferrer"
                        download="loa-upload.xlsx"
                      >
                        Download Template
                      </a>
                    </Col>
                  </Row>

                  <Form>
                    <Dropzone
                      onDrop={acceptedFiles => {
                        handleFileUpload(acceptedFiles)
                      }}
                      accept={{
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                          [],
                      }}
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div className="dropzone">
                          <div
                            className="dz-message needsclick mt-2"
                            {...getRootProps()}
                          >
                            <input {...getInputProps()} />
                            <div className="mb-3">
                              <i className="display-4 text-muted bx bxs-cloud-upload" />
                            </div>
                            <h4>Drop files here or click to upload.</h4>
                          </div>
                        </div>
                      )}
                    </Dropzone>
                    <div className="dropzone-previews mt-3" id="file-previews">
                      {selectedFiles.map((f, i) => {
                        return (
                          <Card
                            className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete"
                            key={i + "-file"}
                          >
                            <div className="p-2">
                              <Row className="align-items-center">
                                <Col className="col-auto">
                                  <i className="mdi mdi-file-delimited font-size-24 font-primary text-success" />
                                </Col>
                                <Col className="col-auto me-4 ">
                                  <Link
                                    to="#"
                                    className="text-muted font-weight-bold"
                                  >
                                    {f.name}
                                  </Link>
                                  <p className="mb-0">
                                    <strong>{f.formattedSize}</strong>
                                  </p>
                                </Col>
                              </Row>
                            </div>
                          </Card>
                        )
                      })}
                    </div>
                  </Form>
                </CardBody>
                <CardBody>
                  {data && data.length > 0 && (
                    <>
                      <div className="mb-4">
                        <FormGroup className="mb-4">
                          <Label for="partnerCockpit">Partner Cockpit:</Label>
                          <Select
                            id="partnerCockpit"
                            value={selectedCockpit}
                            onChange={setSelectedCockpit}
                            options={cockpitOptions}
                            className="react-select"
                            classNamePrefix="select"
                          />
                          <small className="text-muted">
                            Select the partner cockpit where you want to upload
                            the LOA masterfile.
                          </small>
                          {selectedCockpit && !isAdmin && (
                            <small className="text-danger d-block mt-2">
                              You need to be an admin in this partner cockpit to
                              upload LOAs.
                            </small>
                          )}
                        </FormGroup>

                        <div className="d-flex">
                          <button
                            type="button"
                            onClick={_ => setShowLOAs(!showLOAs)}
                            className="btn btn-secondary me-2"
                          >
                            {showLOAs ? "Hide" : "Show"} LOAs
                          </button>
                          <button
                            type="button"
                            onClick={handleSave}
                            className="btn btn-primary ms-2"
                            disabled={!isAdmin}
                          >
                            Upload LOAs
                          </button>
                        </div>
                      </div>
                      {showLOAs && (
                        <Table>
                          <thead>
                            <tr>
                              <th>CPN</th>
                              <th>Part Number</th>
                              <th>Manufacturer</th>
                              <th>Supplier</th>
                              <th>Price</th>
                              <th>MOQ</th>
                              <th>Lead Time</th>
                              <th>Packaging Unit</th>
                              <th>Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((element, index) => (
                              <tr key={index}>
                                <td>{element.cpn}</td>
                                <td>{element.partNumber}</td>
                                <td>{element.manufacturer}</td>
                                <td>{element.supplier}</td>
                                <td>
                                  {element.price?.toFixed(5)}{" "}
                                  {element.price > 0 ? element.currency : ""}
                                </td>
                                <td>{element.moq} pc.</td>
                                <td>{element.leadTime}</td>
                                <td>{element.packagingUnit}</td>
                                <td>{element.notes}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <MessageModal
        show={successModal}
        displayMessage={"Upload successful!"}
        onCloseClick={_ => navigate("/partner-cockpit/part-list")}
        color="success"
        icon="bx bx-check"
      />
    </React.Fragment>
  )
}

export default LOAUpload
